/**
 * The scenarios half of the back-testing page: the scenario types, generated one per outcome
 * of the protocol's compiled graph, and under each the past tickets bucketing placed there and
 * the customers fabricated for it, run one at a time in the simulator or many at once as a bulk
 * run. Selecting a type lights its decision paths in the protocol beside this.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAtomValue, useSetAtom } from 'jotai';
import { ListTree, Loader2, MoreHorizontal, Ticket } from 'lucide-react';
import { toast } from '@/components/ui/sonner';
import { Button } from '@/components/ui/button';
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle
} from '@/components/ui/alert-dialog';
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';
import { aiFunctionService } from '@/services/ai-function.service';
import {
	scenariosBySopAtom,
	loadedTypesBySopAtom,
	activeBranchAtom,
	clearSopAtom,
	invalidateLoadedTypesAtom,
	setScenariosForTypeAtom,
	addScenariosAtom,
	removeScenarioAtom,
	UNTYPED_TYPE_ID
} from '../back-testing.store';
import {
	clearBackTestingTypes,
	reloadBackTestingTypes,
	removeBackTestingType,
	setBackTestingTypes,
	useBackTestingTypes
} from '@/hooks/use-back-testing-types';
import { useTypeReadiness } from '@/hooks/use-type-readiness';
import { endSimulatorSessionAtom, simulatorSessionAtom } from '@/modules/simulator/session/simulator-session.store';
import { useOpenInSimulator } from '@/modules/simulator/session/use-open-in-simulator';
import { resetSimulatorAtom, setBackTestingBucketingHandlerAtom } from '@/stores/websocket.store';
import type { BucketRun } from '@/types/websocket.types';
import { scenarioRunUrl } from '@/constants/paths';
import { useScenarioSelection } from './use-scenario-selection';
import { useStreamFollow, useTypeGeneration } from './use-type-generation';
import { useTypeParams } from './use-type-params';
import { useTypeSearch } from './use-type-search';
import { countsFor, countsLine, deletionOutcome, fabricationRoom, sumCounts, totalOf } from './scenario-counts';
import { AddCasesAction, BulkRunActions, SelectionBar } from './selection-bar';
import { PanelNote } from './panel-note';
import { refusalOf } from './refusal';
import { RefusalNote } from './refusal-note';
import { useHeldRefusal } from './use-held-refusal';
import { FabricateButton } from './fabricate/fabricate-button';
import { FabricateDialog } from './fabricate/fabricate-dialog';
import { FabricatedPreviewRows } from './fabricate/fabricated-preview-rows';
import { useFabrication, type FabricationRequest } from './fabricate/use-fabrication';
import { ScenarioRunnerSheet } from './scenario-runner-sheet';
import { TypesList } from './types-list';
import { TypeDetail } from './type-detail';
import { DetailSkeleton } from './detail-skeleton';
import { LevelHeader } from './level-header';
import { TypeOverview, TypeOverviewEmpty } from './type-overview';
import { PanelState } from '@/modules/simulator/ui/panel-state';
import { PillButton } from '@/modules/simulator/ui/pill-button';
import { RunHistorySheet, type RunHistoryScenario } from '../runs/run-history-sheet';
import { TypeBulkRunsSheet } from '../runs/type-bulk-runs-sheet';
import { TicketConversationDialog } from '@/modules/simulator/conversations/past-ticket';
import { CreateDatasetDialog } from '../datasets/dataset/create-dataset-dialog';
import { ConfirmDeleteDialog } from '../datasets/ui/confirm-delete-dialog';
import { PickingBanner } from '../datasets/picker/picking-banner';
import { useCasePicker } from '../datasets/picker/use-case-picker';
import { fabricatedRunsMocked } from '../runs/modes';
import { counted, failureMessage } from '../format';
import {
	BULK_RUN_MAX_ITEMS,
	toLocalScenario,
	toScenarioCase,
	type Scenario,
	type ScenarioType
} from '../types';

/** The untyped group: past tickets the bucketing placed under no type. */
const GAP_GROUP_ID = UNTYPED_TYPE_ID;

export const SopScenariosPanel = ({
	sopId,
	subcategory,
	onViewTickets
}: {
	sopId: string;
	subcategory?: string | null;
	onViewTickets?: (source: 'mapped' | 'subcategory') => void;
}) => {
	const allScenarios = useAtomValue(scenariosBySopAtom);
	const removeScenario = useSetAtom(removeScenarioAtom);
	const openInSimulator = useOpenInSimulator();
	const setActiveBranch = useSetAtom(activeBranchAtom);
	const clearSop = useSetAtom(clearSopAtom);
	const invalidateLoadedTypes = useSetAtom(invalidateLoadedTypesAtom);
	const setScenariosForType = useSetAtom(setScenariosForTypeAtom);
	const addScenarios = useSetAtom(addScenariosAtom);
	const loadedTypes = useAtomValue(loadedTypesBySopAtom);

	// `isPending` holds until this SOP's types are in, and again whenever another SOP
	// opens, so no empty state shows over data still on its way.
	const { data: sopTypes, isPending: typesLoading, isError: loadFailed } = useBackTestingTypes(sopId);
	const { data: readinessByType } = useTypeReadiness(sopId);
	const hydrated = !typesLoading;
	const loadTypes = useCallback(() => reloadBackTestingTypes(sopId), [sopId]);

	const [clearOpen, setClearOpen] = useState(false);
	const [regenerateOpen, setRegenerateOpen] = useState(false);
	const [fabricateOpen, setFabricateOpen] = useState(false);
	// The type a delete confirmation is asking about; null = nothing pending.
	const [deletingType, setDeletingType] = useState<ScenarioType | null>(null);
	const [deletingFabricated, setDeletingFabricated] = useState<Scenario | null>(null);
	const [runnerOpen, setRunnerOpen] = useState(false);
	const [bucketRun, setBucketRun] = useState<BucketRun | null>(null);
	const [startingBucketing, setStartingBucketing] = useState(false);
	// The conversation behind a clicked ticket chip; null = sheet closed.
	const [threadTicketId, setThreadTicketId] = useState<string | null>(null);
	const [historyScenario, setHistoryScenario] = useState<RunHistoryScenario | null>(null);
	const [datasetDialogOpen, setDatasetDialogOpen] = useState(false);
	const [bulkRunsOpen, setBulkRunsOpen] = useState(false);
	const setBucketingHandler = useSetAtom(setBackTestingBucketingHandlerAtom);
	const session = useAtomValue(simulatorSessionAtom);
	const endSimulatorSession = useSetAtom(endSimulatorSessionAtom);
	const resetSimulator = useSetAtom(resetSimulatorAtom);

	const scenarios = useMemo(() => allScenarios[sopId] ?? [], [allScenarios, sopId]);
	const types = useMemo(() => sopTypes?.types ?? [], [sopTypes]);
	// While a stream is in flight the streamed list IS the list: rows carry their
	// server ids from the first event, so selecting one mid-stream is already
	// selecting the row the persisted set will contain.
	// Refusals stay on the panel, the SOP's under the header and a type's on its scenarios, until dismissed.
	const sopNote = useHeldRefusal(sopId, null);
	const { generatingTypes, streamingTypes, typeStreamEndRef, generateTypes } = useTypeGeneration(sopId, sopNote);
	const displayTypes = streamingTypes ?? types;
	const {
		selectedTypeId,
		selectedScenarioId,
		viewingScenarios,
		reselectRunId,
		pickingForDatasetId,
		setSelectedTypeId,
		setViewingScenarios,
		openScenarios,
		setSelectedScenarioId,
		clearReselect,
		stopPicking
	} = useTypeParams(hydrated && !generatingTypes ? (displayTypes[0]?.id ?? null) : null);
	const selectedType = useMemo(
		() => displayTypes.find((t) => t.id === selectedTypeId) ?? null,
		[displayTypes, selectedTypeId]
	);
	const gapSelected = selectedTypeId === GAP_GROUP_ID;
	// Past tickets the bucketing could place under no type: each may be one the protocol does not cover yet.
	const gapScenarios = useMemo(() => scenarios.filter((s) => s.scenarioTypeId === null), [scenarios]);
	const countsByType = useMemo(() => sopTypes?.counts ?? {}, [sopTypes]);
	/**
	 * From the server's counts: `scenarios` holds only the opened types, so counting it
	 * would understate what Clear all deletes.
	 */
	const totalCounts = useMemo(() => sumCounts(countsByType), [countsByType]);
	const selectedScenarios = useMemo(
		() =>
			selectedType ? scenarios.filter((s) => s.scenarioTypeId === selectedType.id) : gapSelected ? gapScenarios : [],
		[scenarios, selectedType, gapSelected, gapScenarios]
	);
	// From the server's counts, not the rows: they are right before and while the open type's rows arrive.
	const selectedCounts = countsFor(countsByType, selectedTypeId);
	// A scenario id in the URL that no longer resolves (deleted, or a stale link)
	// falls back to its type's list rather than a blank level.
	const selectedScenario = useMemo(
		() => (selectedScenarioId ? (selectedScenarios.find((s) => s.id === selectedScenarioId) ?? null) : null),
		[selectedScenarios, selectedScenarioId]
	);
	useStreamFollow(streamingTypes?.length ?? 0, typeStreamEndRef, Boolean(selectedTypeId));

	// Picking cases for a dataset swaps the bulk-run controls for adding to it, on the dataset's own type
	// only: its cases all come from that one.
	const picker = useCasePicker(pickingForDatasetId, stopPicking);
	const pickingHere = picker !== null && selectedType !== null && picker.dataset.scenarioTypeId === selectedType.id;
	const pickable = useMemo(
		() => (pickingHere ? selectedScenarios.filter((s) => !picker.inDataset.has(s.id)) : selectedScenarios),
		[pickingHere, picker, selectedScenarios]
	);
	// A dataset holds as many cases as one batch runs, so adding to one takes only the room left.
	const selectionMax = pickingHere ? Math.max(0, BULK_RUN_MAX_ITEMS - picker.inDataset.size) : BULK_RUN_MAX_ITEMS;
	const selection = useScenarioSelection(sopId, selectedType?.id ?? null, pickable, selectionMax);
	const canPick = Boolean(selectedType) && totalOf(selectedCounts) > 0 && (!pickingForDatasetId || pickingHere);
	const fabricatedPicked = selectedScenarios.filter(
		(s) => s.kind === 'fabricated' && selection.selectedIds.has(s.id)
	).length;
	// Every capture and run of a dataset is Live data.
	const datasetMockedNote = fabricatedRunsMocked('grounded', fabricatedPicked);
	const pickNote =
		pickingHere && selectionMax === 0
			? `This dataset holds ${BULK_RUN_MAX_ITEMS} cases, the most it can.`
			: fabricatedRunsMocked(pickingHere ? 'grounded' : selection.mode, fabricatedPicked);

	const typeNote = useHeldRefusal(sopId, selectedTypeId);
	const { fabricatingTypeId, previews, fabricate } = useFabrication(sopId, typeNote);

	// Arrived from a bulk run to change what it ran: tick what it ran once the rows have landed.
	// The param is dropped as soon as it is honoured, so a later manual clear is not undone by a re-render.
	const { restoreRun } = selection;
	useEffect(() => {
		if (!reselectRunId || !selectedTypeId || selectedScenarios.length === 0) return;
		let cancelled = false;
		void restoreRun(reselectRunId)
			.catch(() => undefined)
			.finally(() => {
				if (!cancelled) clearReselect();
			});
		return () => {
			cancelled = true;
		};
	}, [reselectRunId, selectedTypeId, selectedScenarios.length, restoreRun, clearReselect]);

	const {
		search: typeSearch,
		setSearch: setTypeSearch,
		terms: typeTerms,
		visibleTypes,
		semantic,
		onSearchKeyDown
	} = useTypeSearch(sopId, displayTypes, setSelectedTypeId);

	// Selecting a type drives the protocol beside this: its decision paths are lit in the text and the graph.
	useEffect(() => {
		setActiveBranch(selectedType?.branch ?? null);
		return () => setActiveBranch(null);
	}, [selectedType, setActiveBranch]);

	// A type's scenarios are fetched once, when it is opened: they carry whole customer
	// threads, and the counts already say how many there are.
	const scenarioSeqRef = useRef(0);
	const [scenariosLoading, setScenariosLoading] = useState(false);
	// A type whose rows failed to arrive is not a type with no rows, and the types query's
	// own error says nothing about them.
	const [scenariosFailed, setScenariosFailed] = useState(false);
	const typeIsLoaded = Boolean(selectedTypeId && (loadedTypes[sopId] ?? []).includes(selectedTypeId));
	/**
	 * Abandons the scenario read in flight, so one issued before a regeneration cannot land
	 * after it with rows the server has just deleted and mark their type loaded.
	 */
	const dropInFlightScenarioRead = useCallback(() => {
		scenarioSeqRef.current += 1;
	}, []);

	const loadScenariosForType = useCallback(
		(key: string) => {
			const seq = ++scenarioSeqRef.current;
			setScenariosLoading(true);
			setScenariosFailed(false);
			return aiFunctionService
				.fetchScenariosForType(sopId, key)
				.then((rows) => {
					if (seq !== scenarioSeqRef.current) return;
					setScenariosForType(
						sopId,
						key === GAP_GROUP_ID ? null : key,
						rows.map((s) => toLocalScenario(sopId, s))
					);
				})
				.catch(() => {
					if (seq !== scenarioSeqRef.current) return;
					setScenariosFailed(true);
					toast.error('Could not load the scenarios for this scenario type');
				})
				.finally(() => {
					if (seq === scenarioSeqRef.current) setScenariosLoading(false);
				});
		},
		[sopId, setScenariosForType]
	);
	useEffect(() => {
		if (!selectedTypeId || !viewingScenarios || typeIsLoaded) return;
		void loadScenariosForType(selectedTypeId);
	}, [selectedTypeId, viewingScenarios, typeIsLoaded, loadScenariosForType]);

	// Read by the bucketing handler, which must not resubscribe on every drill-down.
	const selectedTypeIdRef = useRef(selectedTypeId);
	useEffect(() => {
		selectedTypeIdRef.current = selectedTypeId;
	}, [selectedTypeId]);

	// A bucketing pass started in another tab, or before a reload, is picked up from the server;
	// after that the socket carries its progress. Only this SOP's events matter here.
	useEffect(() => {
		setBucketRun(null);
		void aiFunctionService.fetchBucketing(sopId).then(setBucketRun).catch(() => undefined);
		setBucketingHandler(({ sopId: eventSopId, run }) => {
			if (eventSopId !== sopId) return;
			setBucketRun(run);
			void loadTypes();
			// Only once the pass is over. Reloading per progress tick re-downloads the
			// open type for every chunk, and each read replaces rows that anything
			// writing in the meantime has just added.
			if (run.status === 'done' || run.status === 'failed') {
				// The pass places tickets under types nobody has open. The one on screen
				// is refetched; the rest only have to stop claiming they are loaded.
				invalidateLoadedTypes(sopId);
				if (selectedTypeIdRef.current) void loadScenariosForType(selectedTypeIdRef.current);
			}
			if (run.status === 'failed') toast.error(`Mapping past tickets failed: ${run.error ?? 'unknown error'}`);
			if (run.status === 'done' && run.total === 0) {
				// A pass that could search nowhere says why; one that found nothing left says that.
				if (run.note) toast.message(run.note);
				else toast.success('Every past ticket is already placed or settled');
			}
		});
		return () => setBucketingHandler(null);
	}, [sopId, setBucketingHandler, loadTypes, loadScenariosForType, invalidateLoadedTypes]);

	// A type or scenario id the server did not return (deleted elsewhere, or a stale link)
	// drops back a level once the answer is in, rather than holding a skeleton that never
	// fills. Never mid-generation: the list is being replaced then, and a row briefly
	// missing is not a dead id.
	useEffect(() => {
		if (!hydrated || generatingTypes) return;
		if (selectedTypeId && !gapSelected && !selectedType) {
			setSelectedTypeId(null);
			return;
		}
		// Only once the type's own rows are in: the types answer first, and judging a
		// scenario against a list that has not arrived drops it off a deep link.
		if (selectedScenarioId && typeIsLoaded && !selectedScenario) setSelectedScenarioId(null);
	}, [
		hydrated,
		generatingTypes,
		selectedScenarioId,
		selectedScenario,
		selectedTypeId,
		selectedType,
		typeIsLoaded,
		gapSelected,
		setSelectedScenarioId,
		setSelectedTypeId
	]);

	const bucketing = bucketRun?.status === 'running';
	// A widened pass searched the subcategory beyond the SOP's mapped tickets; the
	// button says so, and says how far it has got while it runs.
	const widened = Boolean(bucketRun?.wide);

	// The socket is the fast path, not the only one: if it drops mid-pass the button would
	// hold its last count forever, so a running pass is also polled, and the rows reload
	// once the poll sees it finish.
	useEffect(() => {
		if (!bucketing) return;
		const poll = setInterval(() => {
			void aiFunctionService
				.fetchBucketing(sopId)
				.then((run) => {
					if (!run) return;
					setBucketRun(run);
					// The socket handler's finish, for when the socket missed it.
					if (run.status !== 'running') {
						void loadTypes();
						invalidateLoadedTypes(sopId);
						if (selectedTypeIdRef.current) void loadScenariosForType(selectedTypeIdRef.current);
					}
				})
				.catch(() => undefined);
		}, 10_000);
		return () => clearInterval(poll);
	}, [bucketing, sopId, loadTypes, loadScenariosForType, invalidateLoadedTypes]);

	// One button: bucket whatever past tickets are not placed yet. A pass in progress is
	// never restarted; the server reports it instead.
	const handleFindPastTickets = useCallback(async () => {
		setStartingBucketing(true);
		try {
			setBucketRun(await aiFunctionService.startBucketing(sopId));
		} catch (err) {
			toast.error(failureMessage(err, 'Could not map past tickets'));
		} finally {
			setStartingBucketing(false);
		}
	}, [sopId]);

	// The type's other scenarios travel with the one opened, so the simulator can move
	// between siblings without a round trip back here.
	const handleOpen = useCallback(
		(scenario: Scenario) => {
			const siblings = scenarios.filter((s) => s.scenarioTypeId === scenario.scenarioTypeId);
			openInSimulator({
				cases: siblings.map(toScenarioCase),
				activeIndex: Math.max(0, siblings.findIndex((s) => s.id === scenario.id)),
				origin: { sopId }
			});
			setRunnerOpen(true);
		},
		[sopId, scenarios, openInSimulator]
	);

	// The server refuses to delete what a dataset uses, so the screen changes only once it agrees.
	const deleteScenario = useCallback(
		(scenario: Scenario) => {
			void aiFunctionService.deleteScenario(sopId, scenario.id).then(
				() => {
					removeScenario(sopId, scenario.id);
					selection.drop(scenario.id);
					void loadTypes();
				},
				(err) => toast.error(failureMessage(err, 'Could not delete this scenario'))
			);
		},
		[removeScenario, sopId, loadTypes, selection]
	);
	// A past ticket comes back with the next Find more; a fabricated scenario never does, so only that one asks.
	const handleDelete = useCallback(
		(scenario: Scenario) => (scenario.kind === 'fabricated' ? setDeletingFabricated(scenario) : deleteScenario(scenario)),
		[deleteScenario]
	);

	// No navigation here: once the type leaves the list, the dead-id effect above drops it from the URL.
	const { hold: holdForType, dismiss: dismissForType } = typeNote;
	const handleDeleteType = useCallback(
		(typeId: string) => {
			dismissForType();
			void aiFunctionService.deleteScenarioType(sopId, typeId).then(
				() => removeBackTestingType(sopId, typeId),
				(err) => holdForType({ sopId, typeId }, refusalOf(err, 'Could not delete this scenario type'))
			);
		},
		[sopId, holdForType, dismissForType]
	);

	const regenerate = useCallback(() => {
		dropInFlightScenarioRead();
		void generateTypes({
			// Regenerating deletes every scenario under the old set, and the untyped
			// group's key outlives it, so without this its rows stay marked loaded and the
			// list shows tickets that no longer exist.
			onReplaced: (next) => {
				clearSop(sopId);
				setBackTestingTypes(sopId, next);
			},
			onMapPastTickets: handleFindPastTickets
		});
	}, [dropInFlightScenarioRead, generateTypes, clearSop, sopId, handleFindPastTickets]);

	const handleFabricate = useCallback(
		(typeId: string, request: FabricationRequest) => void fabricate(typeId, request, addScenarios),
		[fabricate, addScenarios]
	);

	const level: 'types' | 'scenarios' = selectedTypeId && viewingScenarios ? 'scenarios' : 'types';
	const fabricateButton = selectedType && (
		<FabricateButton
			atCap={fabricationRoom(selectedCounts) === 0}
			busy={fabricatingTypeId === selectedType.id}
			disabled={fabricatingTypeId !== null}
			onClick={() => setFabricateOpen(true)}
		/>
	);

	return (
		<div className='flex min-h-0 flex-1 flex-col gap-2'>
			{picker && <PickingBanner picker={picker} onItsType={pickingHere} />}

			{level === 'types' && (
				<LevelHeader
					title={
						streamingTypes !== null
							? `${counted(streamingTypes.length, 'scenario type')} mapped so far…`
							: types.length
								? `${counted(types.length, 'scenario type')} · ${countsLine(totalCounts)}`
								: 'Scenario types'
					}
					actions={
						<>
							{types.length > 0 && !generatingTypes && (
								<PillButton disabled={bucketing || startingBucketing} onClick={handleFindPastTickets}>
									{bucketing || startingBucketing ? (
										<Loader2 className='size-3 animate-spin' />
									) : (
										<Ticket className='size-3' />
									)}
									{bucketing && bucketRun
										? `${bucketRun.done}/${bucketRun.total || '…'}`
										: widened
											? 'Find more'
											: 'Find past tickets'}
								</PillButton>
							)}
							<PillButton
								variant='primary'
								// Replacing a set deletes every scenario under it, so only a first set goes without asking.
								onClick={() => (types.length ? setRegenerateOpen(true) : regenerate())}
								// Never while the types are unknown: replacing a set the server may still hold
								// destroys every scenario bucketed under it.
								disabled={generatingTypes || loadFailed || (!hydrated && !types.length)}
							>
								{generatingTypes ? <Loader2 className='size-3 animate-spin' /> : <ListTree className='size-3' />}
								{types.length ? 'Regenerate' : 'Generate types'}
							</PillButton>
							{/* The rest is rare or destructive, so it sits one click away rather than
							    competing with the two things this screen is for. */}
							<DropdownMenu>
								<DropdownMenuTrigger asChild>
									<Button variant='ghost' size='icon' className='size-7 text-gray-900' aria-label='More'>
										<MoreHorizontal className='size-3.5' />
									</Button>
								</DropdownMenuTrigger>
								<DropdownMenuContent align='end'>
									<DropdownMenuItem onClick={() => onViewTickets?.('mapped')}>View mapped tickets</DropdownMenuItem>
									{subcategory && (
										<DropdownMenuItem onClick={() => onViewTickets?.('subcategory')}>
											View subcategory tickets
										</DropdownMenuItem>
									)}
									{(types.length > 0 || scenarios.length > 0) && !generatingTypes && (
										<DropdownMenuItem variant='destructive' onClick={() => setClearOpen(true)}>
											Clear all
										</DropdownMenuItem>
									)}
								</DropdownMenuContent>
							</DropdownMenu>
						</>
					}
				/>
			)}

			{level === 'types' && sopNote.refusal && (
				<RefusalNote refusal={sopNote.refusal} onDismiss={sopNote.dismiss} className='px-4 py-1.5' />
			)}

			{level === 'types' && bucketRun?.note && bucketRun.status !== 'running' && (
				<PanelNote className='px-4 py-1.5'>{bucketRun.note}</PanelNote>
			)}

			{level === 'types' && displayTypes.length === 0 && !generatingTypes && !hydrated && (
				<PanelState icon={<Loader2 className='size-4 animate-spin' />}>Loading scenario types</PanelState>
			)}

			{/* Types that could not be fetched are not an SOP without types. Saying "none yet"
			    there invites the one action that would make it true: generating replaces the set,
			    and the server drops every scenario under the old one. */}
			{level === 'types' && displayTypes.length === 0 && !generatingTypes && hydrated && (
				<PanelState icon={<ListTree className='size-5' />}>
					<span className='font-medium text-text-body'>
						{loadFailed ? 'The scenario types could not be loaded' : 'No scenario types yet'}
					</span>
					<span className='max-w-sm'>
						{loadFailed
							? 'This says nothing about whether the protocol has any. Reload to try again.'
							: 'Generating writes one scenario type for every outcome of the protocol’s compiled graph. Compile the protocol first if it has changed since.'}
					</span>
				</PanelState>
			)}

			{level === 'types' && (displayTypes.length > 0 || generatingTypes) && (
				<div className='grid min-h-0 flex-1 grid-cols-[minmax(240px,40%)_minmax(0,1fr)]'>
					<div className='flex min-h-0 min-w-0 flex-col border-r border-gray-500'>
						<TypesList
							types={visibleTypes}
							allTypes={displayTypes}
							terms={typeTerms}
							countsByType={countsByType}
							readinessByType={readinessByType}
							generating={generatingTypes}
							search={typeSearch}
							onSearchChange={setTypeSearch}
							onSearchKeyDown={onSearchKeyDown}
							semantic={semantic}
							streamEndRef={typeStreamEndRef}
							selectedId={selectedTypeId}
							onSelect={setSelectedTypeId}
							// The untyped group describes nothing, so it goes straight to its tickets.
							onSelectGap={() => openScenarios(GAP_GROUP_ID)}
						/>
					</div>
					<div className='flex min-h-0 min-w-0 flex-col'>
						{selectedType ? (
							<TypeOverview
								type={selectedType}
								counts={selectedCounts}
								readiness={readinessByType?.[selectedType.id]}
								onOpenScenarios={() => setViewingScenarios(true)}
								onOpenBulkRuns={() => setBulkRunsOpen(true)}
							/>
						) : (
							<TypeOverviewEmpty />
						)}
					</div>
				</div>
			)}

			{level === 'scenarios' && (selectedType || gapSelected) && (
				<TypeDetail
					type={selectedType}
					scenarios={selectedScenarios}
					counts={selectedCounts}
					hydrated={hydrated}
					loadingScenarios={scenariosLoading}
					loadFailed={loadFailed || scenariosFailed}
					activeScenario={selectedScenario}
					selectedIds={selection.selectedIds}
					selectionFull={selection.selectedIds.size >= selectionMax}
					onToggleSelected={canPick ? selection.toggle : undefined}
					inDatasetIds={pickingHere ? picker.inDataset : undefined}
					fabricate={fabricateButton}
					notice={
						typeNote.refusal && (
							<RefusalNote
								refusal={typeNote.refusal}
								onDismiss={typeNote.dismiss}
								className='border-b border-gray-500 px-4 py-1.5'
							/>
						)
					}
					streaming={
						selectedType && fabricatingTypeId === selectedType.id ? <FabricatedPreviewRows previews={previews} /> : null
					}
					footer={
						canPick && (
							<SelectionBar
								selected={selection.selectedIds.size}
								available={pickingHere ? pickable.length : totalOf(selectedCounts)}
								max={selectionMax}
								note={pickNote}
								onSelectFirst={selection.selectFirst}
								onClear={selection.clear}
							>
								{pickingHere ? (
									<AddCasesAction
										selected={selection.selectedIds.size}
										adding={selection.addingCases}
										onAdd={() => void selection.addCases(picker.dataset.id)}
									/>
								) : (
									<BulkRunActions
										selected={selection.selectedIds.size}
										mode={selection.mode}
										onModeChange={selection.setMode}
										starting={selection.starting}
										onRun={selection.run}
										onCreateDataset={() => setDatasetDialogOpen(true)}
									/>
								)}
							</SelectionBar>
						)
					}
					onBack={() => (gapSelected ? setSelectedTypeId(null) : setViewingScenarios(false))}
					onOpenBulkRuns={() => setBulkRunsOpen(true)}
					onDeleteType={() => selectedType && setDeletingType(selectedType)}
					onOpenScenario={(scenario) => setSelectedScenarioId(scenario.id)}
					onTestScenario={handleOpen}
					onDeleteScenario={handleDelete}
					onViewTicket={setThreadTicketId}
					onHistory={gapSelected ? undefined : setHistoryScenario}
				/>
			)}

			{/* Deep-linked into a level whose rows have not arrived yet. */}
			{level === 'scenarios' && !selectedType && !gapSelected && (
				<DetailSkeleton onBack={() => setSelectedTypeId(null)} label='Back to scenario types' />
			)}

			{/* Deleting a type takes its scenarios with it on the server, so it asks first, as Clear all does. */}
			<AlertDialog open={deletingType !== null} onOpenChange={(open) => !open && setDeletingType(null)}>
				<AlertDialogContent>
					<AlertDialogHeader>
						<AlertDialogTitle>Delete this scenario type?</AlertDialogTitle>
						<AlertDialogDescription>
							Removes the type and every scenario under it. {deletionOutcome(countsFor(countsByType, deletingType?.id ?? null))}{' '}
							The other types are untouched.
						</AlertDialogDescription>
					</AlertDialogHeader>
					<AlertDialogFooter>
						<AlertDialogCancel>Cancel</AlertDialogCancel>
						<AlertDialogAction
							onClick={() => {
								if (deletingType) handleDeleteType(deletingType.id);
								setDeletingType(null);
							}}
						>
							Delete
						</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>

			<AlertDialog open={regenerateOpen} onOpenChange={setRegenerateOpen}>
				<AlertDialogContent>
					<AlertDialogHeader>
						<AlertDialogTitle>Regenerate the scenario types?</AlertDialogTitle>
						<AlertDialogDescription>
							Replaces the {counted(types.length, 'scenario type')} with a new set from the protocol&apos;s compiled graph,
							and removes every scenario of this protocol. {deletionOutcome(totalCounts)}
						</AlertDialogDescription>
					</AlertDialogHeader>
					<AlertDialogFooter>
						<AlertDialogCancel>Cancel</AlertDialogCancel>
						<AlertDialogAction
							onClick={() => {
								setRegenerateOpen(false);
								regenerate();
							}}
						>
							Regenerate
						</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>

			{selectedType && (
				<FabricateDialog
					open={fabricateOpen}
					room={fabricationRoom(selectedCounts)}
					onOpenChange={setFabricateOpen}
					onFabricate={(request) => handleFabricate(selectedType.id, request)}
				/>
			)}

			<ScenarioRunnerSheet open={runnerOpen} onOpenChange={setRunnerOpen} runUrl={scenarioRunUrl} />
			<TicketConversationDialog ticketId={threadTicketId} onOpenChange={(open) => !open && setThreadTicketId(null)} />
			<ConfirmDeleteDialog
				open={deletingFabricated !== null}
				title='Delete this fabricated scenario?'
				onOpenChange={(open) => !open && setDeletingFabricated(null)}
				onConfirm={() => deletingFabricated && deleteScenario(deletingFabricated)}
			>
				It is gone for good: a fabricated scenario cannot be found again the way a past ticket can.
			</ConfirmDeleteDialog>
			<CreateDatasetDialog
				open={datasetDialogOpen}
				count={selection.selectedIds.size}
				suggestedName={selectedType?.description ?? ''}
				note={datasetMockedNote}
				creating={selection.creatingDataset}
				onOpenChange={setDatasetDialogOpen}
				onCreate={(name, description) => void selection.createDataset(name, description)}
			/>
			<RunHistorySheet
				sopId={sopId}
				scenario={historyScenario}
				onOpenChange={(open) => !open && setHistoryScenario(null)}
				onOpenTicket={setThreadTicketId}
			/>
			{selectedType && (
				<TypeBulkRunsSheet
					sopId={sopId}
					typeId={selectedType.id}
					open={bulkRunsOpen}
					onOpenChange={setBulkRunsOpen}
					currentRunId={null}
				/>
			)}

			<AlertDialog open={clearOpen} onOpenChange={setClearOpen}>
				<AlertDialogContent>
					<AlertDialogHeader>
						<AlertDialogTitle>Clear everything for this protocol?</AlertDialogTitle>
						<AlertDialogDescription>
							Removes {counted(types.length, 'scenario type')} and every scenario of this protocol.{' '}
							{deletionOutcome(totalCounts)} Nothing on the SOP itself changes, and types can be generated again.
						</AlertDialogDescription>
					</AlertDialogHeader>
					<AlertDialogFooter>
						<AlertDialogCancel>Cancel</AlertDialogCancel>
						<AlertDialogAction
							onClick={() => {
								setClearOpen(false);
								sopNote.dismiss();
								void aiFunctionService.clearBackTesting(sopId).then(
									() => {
										clearSop(sopId);
										clearBackTestingTypes(sopId);
										// The runner may still hold these scenarios: its loaded case, the runs it kept
										// per case and the persisted session all refer to rows that no longer exist.
										setRunnerOpen(false);
										if (!session?.origin?.sopId || session.origin.sopId === sopId) {
											resetSimulator();
											endSimulatorSession();
										}
										toast.success('Cleared');
									},
									(err) => sopNote.hold({ sopId, typeId: null }, refusalOf(err, 'Could not clear this SOP'))
								);
							}}
						>
							Clear all
						</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>
		</div>
	);
};
